import React, { useEffect, useMemo, useState } from 'react';
import { Button, message, Table } from 'antd';
import Icon, { InfoCircleTwoTone, PlusOutlined } from '@ant-design/icons';
import { intersectionBy } from 'lodash';
import { Moment } from 'moment';

import deployLined from '@icons/emp/deploy.svg';
import { useDeployCandidateList } from '@api/emp/queryHooks';
import { EDataTransformDimensionName } from '@src/common/constant/enum';
import { SearchFilterPanel } from '@components/common/ManagementTable/components/SearchFilterPanel';
import { getSelectiveDeploymentOptions } from '@src/emp-3.0/utils/buildOptions';

import { StyledDrawer, StyledHeader, StyledSuccessText, StyledTextBtn, Footer } from './styled';
import { IDeploymentProps } from './typed';
import { DEFAULT_VALUE } from './constants';
import DeploymentTable from './DeploymentTable';
import { extractWorkflowIdFromUrl } from './utils';
import {
  generateDateColumn,
  generateNameColumn,
  generateTypeColumn,
  generateUserColumn,
} from './generateColumn';
import { generateDeploymentTableFilterData } from './searchFilterData';
import { useNavigate } from 'react-router-dom';

const DeploymentDrawer: React.FC<IDeploymentProps> = ({
  loading,
  deploySuccess,
  drawerProps = {},
  onDeploy,
  metricType,
  enableSecondPanel,
}) => {
  const url = window.location.href;
  const id = extractWorkflowIdFromUrl(url);
  const navigate = useNavigate();
  const { open, onClose } = drawerProps;

  const [messageApi, contextHolder] = message.useMessage();
  const { data: deployListData, isFetching: isFetchingDeployList } = useDeployCandidateList();

  const [selectedRows, setSelectedRows] = useState<any[]>([]);
  const [selectedRowKeys, setSelectedRowKeys] = useState<any[]>([]);
  const [searchText, setSearchText] = useState<string>('');
  // deployment filters
  const [showFilter, setShowFilter] = useState<boolean>(false);
  const [deploymentType, setDeploymentType] = useState<string>(DEFAULT_VALUE);
  const [updatedBy, setUpdatedby] = useState<string>(DEFAULT_VALUE);
  const [updateDate, setUpdateDate] = useState<Moment[]>([]);
  const [sort, setSort] = useState<{ key: string; order: number }>({ key: '', order: 0 });

  const enableSuccessPanel = enableSecondPanel ? deploySuccess : false;

  const { typeOptions, updatedByOptions } = getSelectiveDeploymentOptions(
    deployListData?.data,
    'All',
  );

  const dataSource = useMemo(() => {
    const {
      data: resultData,
      selectedRowKeys: defaultSelectedRowKeys,
      selectedRows: defaultSelectedRows,
    } = generateDeploymentTableFilterData(
      deployListData?.data,
      {
        searchText,
      },
      {
        type: deploymentType,
        updatedBy,
        updateDate,
      },
      sort,
      id,
    );

    if (defaultSelectedRows.length > 0) {
      setSelectedRowKeys(defaultSelectedRowKeys);
      setSelectedRows(defaultSelectedRows);
    }

    return resultData;
  }, [deployListData?.data, searchText, deploymentType, updatedBy, updateDate, sort, id]);

  const selectedRowsAfterFilter = useMemo(
    () => intersectionBy(selectedRows, dataSource, 'id'),
    [dataSource, selectedRows],
  );

  const hasSelectedRows = useMemo(
    () => selectedRowsAfterFilter.length > 0,
    [selectedRowsAfterFilter.length],
  );
  const isDeployListAvailable = deployListData?.data?.length > 0;

  useEffect(() => {
    if (open && !isFetchingDeployList && !isDeployListAvailable) {
      messageApi.info({
        content:
          'There are no updates to deploy right now. Feel free to create some new metrics or dimensions!',
        icon: <InfoCircleTwoTone />,
      });
      if (!enableSuccessPanel) {
        // @ts-ignore
        onClose?.();
      }
    }
  }, [open, isDeployListAvailable, isFetchingDeployList, messageApi, enableSuccessPanel]);

  const handleDeployClick = () => {
    const initializeDeployData = () => ({
      ids: [] as number[],
      deploy_all: false,
      flag_to_deploy: false,
    });

    const deployData = {
      mapped_events: initializeDeployData(),
      metrics: initializeDeployData(),
      data_transform: initializeDeployData(),
      custom_dimension: initializeDeployData(),
      flow: initializeDeployData(),
    };

    selectedRowsAfterFilter.forEach((item) => {
      const { type, id } = item;
      if (deployData[type]) {
        deployData[type].ids.push(id);
      }
    });
    onDeploy?.(deployData);
  };

  const resetFilters = () => {
    setDeploymentType(DEFAULT_VALUE);
    setUpdatedby(DEFAULT_VALUE);
    setUpdateDate([]);
  };

  const columns = [
    Table.SELECTION_COLUMN,
    Table.EXPAND_COLUMN,
    generateNameColumn(),
    generateTypeColumn({
      column: {
        showFilter,
        options: typeOptions,
        value: deploymentType,
        onChange: (value: string) => setDeploymentType(value),
        sortEnabled: true,
        sort,
        onSortChange: setSort,
        headerStyle: { padding: 0 },
      },
    }),
    generateUserColumn({
      column: {
        title: 'Latest updated by',
        dataKey: 'last_updated_by',
        showFilter,
        options: updatedByOptions,
        value: updatedBy,
        onChange: (value: string) => setUpdatedby(value),
        sortEnabled: true,
        sort,
        onSortChange: setSort,
      },
      dataIndex: 'last_updated_by',
      key: 'last_updated_by',
    }),
    generateDateColumn({
      column: {
        title: 'Latest updated at',
        dataKey: 'edited_time',
        showFilter,
        value: updateDate,
        onChange: setUpdateDate,
        sortEnabled: true,
        sort,
        onSortChange: setSort,
      },
      dataIndex: 'edited_time',
      key: 'edited_time',
    }),
  ];

  const modalFooter = (
    <Footer>
      <Button onClick={onClose}>Cancel</Button>
      <Button
        type="primary"
        style={
          hasSelectedRows
            ? undefined
            : {
                color: '#fff',
                backgroundColor: '#0046ff',
                borderColor: 'transparent',
                opacity: 0.4,
              }
        }
        onClick={handleDeployClick}
        disabled={!hasSelectedRows}
        loading={loading}
      >
        Deploy
      </Button>
    </Footer>
  );
  return (
    <>
      {contextHolder}
      {(isDeployListAvailable || enableSuccessPanel) && (
        <StyledDrawer
          width={enableSuccessPanel ? 600 : 1200}
          destroyOnClose
          {...drawerProps}
          title={
            <StyledHeader>
              <div>
                <Icon component={deployLined} style={{ marginRight: '8px' }} />
                <span>{enableSuccessPanel ? 'Confirm Deployment' : 'Deployment Confirmation'}</span>
              </div>
              {enableSuccessPanel ? null : (
                <SearchFilterPanel
                  style={{ height: '24px', width: 'auto' }}
                  showFilter={showFilter}
                  onFilterVisibilityChange={(value) => {
                    resetFilters();
                    setShowFilter(value);
                  }}
                  searchText={searchText}
                  onSearch={setSearchText}
                />
              )}
            </StyledHeader>
          }
          footer={enableSuccessPanel ? null : modalFooter}
        >
          {enableSuccessPanel && (
            <div style={{ padding: 16 }}>
              <StyledSuccessText>Deployment Successful!</StyledSuccessText>
              <p>
                Full traffic monitoring and drill-down analysis for the
                {selectedRows
                  .map((i) => `"${EDataTransformDimensionName[i.name] ?? i.name}"`)
                  .join(', ')}{' '}
                are now available on Pulse.
              </p>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <StyledTextBtn
                  onClick={(e) => {
                    onClose?.(e);
                    navigate(`/workflow/${metricType}/new`, { replace: true });
                  }}
                >
                  <PlusOutlined />
                  <span>{`Create another ${metricType}`}</span>
                </StyledTextBtn>
                <Button type="primary" onClick={onClose}>
                  Done
                </Button>
              </div>
            </div>
          )}
          {!enableSuccessPanel && (
            <DeploymentTable
              loading={isFetchingDeployList || loading}
              tableProps={{
                columns,
                dataSource,
                rowSelection: {
                  selectedRowKeys: selectedRowKeys,
                  onChange: (selectedRowKeys, selectedRows, { type }) => {
                    setSelectedRowKeys(selectedRowKeys);
                    setSelectedRows(selectedRows);
                  },
                },
              }}
            />
          )}
        </StyledDrawer>
      )}
    </>
  );
};

export default DeploymentDrawer;
